import React, { useState } from 'react';
import { Plus, Check, Trash2, X, Calendar, Bell, Flag, Folder, Tag } from 'lucide-react';
import type { Task, Subtask, Priority, Project, RecurrenceRule } from '@flowfocus/core';
import { PRIORITY_COLORS, localDateKey } from '@flowfocus/core';
import { Sheet, Stepper, SectionLabel } from './ui';

const PRIORITIES: { id: Priority; label: string }[] = [
  { id: 'high', label: 'Alta' }, { id: 'medium', label: 'Media' },
  { id: 'low', label: 'Baja' }, { id: 'none', label: 'Sin' },
];
const RECURRENCE: { id: RecurrenceRule; label: string }[] = [
  { id: 'none', label: 'Nunca' }, { id: 'daily', label: 'Diaria' },
  { id: 'weekdays', label: 'L-V' }, { id: 'weekly', label: 'Semanal' },
];

const toDate = (ts?: number) => (ts ? localDateKey(ts) : '');
const toTime = (ts?: number) => {
  if (!ts) return '';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export function TaskDetail({ task, projects, onClose, onUpdate, onDelete }: {
  task: Task; projects: Project[]; onClose: () => void;
  onUpdate: (c: Partial<Task>) => void; onDelete: () => void;
}) {
  const [name, setName] = useState(task.name);
  const [notes, setNotes] = useState(task.notes ?? '');
  const [newSub, setNewSub] = useState('');
  const [newTag, setNewTag] = useState('');
  const commit = onUpdate;

  const addTag = () => {
    const t = newTag.trim().replace(/^#/, '');
    if (!t || task.tags.includes(t)) { setNewTag(''); return; }
    commit({ tags: [...task.tags, t] });
    setNewTag('');
  };

  const addSub = () => {
    if (!newSub.trim()) return;
    const sub: Subtask = { id: crypto.randomUUID(), name: newSub.trim(), completed: false, estimatedPomodoros: 1, completedPomodoros: 0 };
    commit({ subtasks: [...task.subtasks, sub] });
    setNewSub('');
  };

  const setDue = (v: string) => commit({ dueDate: v ? new Date(v + 'T00:00:00').getTime() : undefined });
  const setReminder = (v: string) => {
    if (!v) return commit({ reminder: undefined });
    const base = task.dueDate ? new Date(task.dueDate) : new Date();
    const [h, m] = v.split(':').map(Number);
    base.setHours(h, m, 0, 0);
    commit({ reminder: base.getTime() });
  };

  return (
    <Sheet title="Tarea" onClose={onClose}
      headerRight={<button className="icon-btn" style={{ width: 30, height: 30 }} onClick={onClose}><X size={16} /></button>}>
      <input className="input-bare" style={{ fontSize: 18, fontWeight: 600 }} value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => name.trim() && name !== task.name && commit({ name: name.trim() })}
        placeholder="Nombre de la tarea" />

      <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
        onBlur={() => notes !== (task.notes ?? '') && commit({ notes })}
        placeholder="Notas…" rows={2}
        style={{ width: '100%', resize: 'none', border: 'none', outline: 'none', background: 'var(--fill-2)', borderRadius: 10, padding: '10px 12px', fontSize: 14, color: 'var(--text-pri)', marginTop: 10 }} />

      <SectionLabel>Prioridad</SectionLabel>
      <div className="seg">
        {PRIORITIES.map((p) => (
          <button key={p.id} className={task.priority === p.id ? 'on' : ''} onClick={() => commit({ priority: p.id })}>
            <Flag size={11} style={{ marginRight: 3, verticalAlign: '-1px' }} fill={task.priority === p.id ? PRIORITY_COLORS[p.id] : 'none'} color={PRIORITY_COLORS[p.id]} />
            {p.label}
          </button>
        ))}
      </div>

      <div className="card" style={{ borderRadius: 11, marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px' }}>
        <span style={{ fontSize: 14 }}>Pomodoros estimados</span>
        <Stepper value={task.estimatedPomodoros} min={1} max={20} onChange={(v) => commit({ estimatedPomodoros: v })} />
      </div>

      <SectionLabel>Fecha y recordatorio</SectionLabel>
      <div className="card" style={{ borderRadius: 11, overflow: 'hidden' }}>
        <label style={rowStyle}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}><Calendar size={15} color="var(--text-mut)" /> Vencimiento</span>
          <input type="date" value={toDate(task.dueDate)} onChange={(e) => setDue(e.target.value)} style={dateInput} />
        </label>
        <label style={{ ...rowStyle, borderTop: '0.5px solid var(--border)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}><Bell size={15} color="var(--text-mut)" /> Recordatorio</span>
          <input type="time" value={toTime(task.reminder)} onChange={(e) => setReminder(e.target.value)} style={dateInput} />
        </label>
      </div>

      <SectionLabel>Repetir</SectionLabel>
      <div className="seg">
        {RECURRENCE.map((r) => (
          <button key={String(r.id)} className={task.recurrence === r.id ? 'on' : ''} onClick={() => commit({ recurrence: r.id })}>{r.label}</button>
        ))}
      </div>

      <SectionLabel>Proyecto</SectionLabel>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button className="chip" onClick={() => commit({ projectId: undefined })}
          style={{ padding: '6px 11px', cursor: 'pointer', border: 'none', background: !task.projectId ? 'var(--primary-soft)' : 'var(--fill)', color: !task.projectId ? 'var(--primary)' : 'var(--text-sec)' }}>
          <Folder size={12} /> Ninguno
        </button>
        {projects.map((p) => (
          <button key={p.id} className="chip" onClick={() => commit({ projectId: p.id })}
            style={{ padding: '6px 11px', cursor: 'pointer', border: 'none', background: task.projectId === p.id ? `${p.color}22` : 'var(--fill)', color: task.projectId === p.id ? p.color : 'var(--text-sec)' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color }} /> {p.name}
          </button>
        ))}
      </div>

      <SectionLabel>Etiquetas / áreas</SectionLabel>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {task.tags.map((tag) => (
          <span key={tag} className="chip" style={{ background: 'var(--primary-soft)', color: 'var(--primary)', padding: '6px 6px 6px 10px' }}>
            <Tag size={11} /> {tag}
            <button onClick={() => commit({ tags: task.tags.filter((x) => x !== tag) })}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--primary)', display: 'flex', padding: 0, marginLeft: 2 }}>
              <X size={12} />
            </button>
          </span>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); addTag(); }} style={{ display: 'inline-flex' }}>
          <input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="+ etiqueta"
            style={{ background: 'var(--fill)', border: 'none', outline: 'none', borderRadius: 20, padding: '6px 12px', fontSize: 12, color: 'var(--text-pri)', width: 110 }} />
        </form>
      </div>

      <SectionLabel>Subtareas</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
        {task.subtasks.map((s) => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 2px' }}>
            <button onClick={() => commit({ subtasks: task.subtasks.map((x) => (x.id === s.id ? { ...x, completed: !x.completed } : x)) })}
              style={{ width: 19, height: 19, borderRadius: '50%', flexShrink: 0, cursor: 'pointer', border: `1.5px solid ${s.completed ? 'var(--green)' : 'var(--text-mut)'}`, background: s.completed ? 'var(--green)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {s.completed && <Check size={11} color="#fff" strokeWidth={3} />}
            </button>
            <span style={{ flex: 1, fontSize: 14, textDecoration: s.completed ? 'line-through' : 'none', color: s.completed ? 'var(--text-mut)' : 'var(--text-pri)' }}>{s.name}</span>
            <button className="btn-ghost" style={{ padding: 4 }} onClick={() => commit({ subtasks: task.subtasks.filter((x) => x.id !== s.id) })}><Trash2 size={13} color="var(--text-mut)" /></button>
          </div>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); addSub(); }} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="submit" className="icon-btn" style={{ width: 28, height: 28, flexShrink: 0 }}><Plus size={16} /></button>
        <input className="input-bare" value={newSub} onChange={(e) => setNewSub(e.target.value)} placeholder="Añadir subtarea" />
      </form>

      <button className="btn-ghost" style={{ width: '100%', justifyContent: 'center', color: 'var(--danger)', marginTop: 18 }}
        onClick={() => { onDelete(); onClose(); }}>
        <Trash2 size={15} /> Eliminar tarea
      </button>
    </Sheet>
  );
}

const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', cursor: 'pointer' };
const dateInput: React.CSSProperties = { border: 'none', background: 'transparent', color: 'var(--text-pri)', fontSize: 14 };
